import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { T } from '../styles/tokens';

type NavId = 'dashboard' | 'purchase' | 'payments' | 'referral' | 'instructions' | 'support';

interface Props {
  active: NavId;
}

const NAV: { id: NavId; label: string; mono: string; path: string }[] = [
  { id: 'dashboard', label: 'Подписки', mono: '01', path: '/dashboard' },
  { id: 'purchase', label: 'Купить', mono: '02', path: '/purchase' },
  { id: 'payments', label: 'Платежи', mono: '03', path: '/payments' },
  { id: 'referral', label: 'Реферальная', mono: '04', path: '/referral' },
  { id: 'instructions', label: 'Инструкции', mono: '05', path: '/instructions' },
  { id: 'support', label: 'Поддержка', mono: '06', path: '/support' },
];

export default function Sidebar({ active }: Props) {
  const navigate = useNavigate();

  return (
    <aside style={s.sidebar}>
      <div style={s.sidebarTop} onClick={() => navigate('/')}>
        <span style={s.logoBox}>VS</span>
        <span style={s.logoName}>VPN Saler</span>
      </div>
      <div style={s.navSection}>
        <div style={s.navLabel}>// навигация</div>
        <nav>
          {NAV.map((item) => (
            <button
              key={item.id}
              style={{ ...s.navItem, ...(active === item.id ? s.navItemActive : {}) }}
              onClick={() => navigate(item.path)}
            >
              <span style={s.navMono}>{item.mono}</span>
              <span style={s.navText}>{item.label}</span>
              {active === item.id && <span style={s.navArrow}>→</span>}
            </button>
          ))}
        </nav>
      </div>
      <div style={s.sidebarFooter}>
        <div style={s.userRow}>
          <div style={s.userAva}>АИ</div>
          <div style={s.userInfo}>
            <div style={s.userName}>Алексей И.</div>
            <div style={s.userEmail}>alex@mail.ru</div>
          </div>
        </div>
        <button
          style={s.logoutBtn}
          onClick={() => {
            localStorage.removeItem('vpnsaler_access');
            navigate('/');
          }}
          title="Выйти"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </aside>
  );
}

const s: Record<string, CSSProperties> = {
  sidebar: {
    width: 220,
    background: T.surface,
    borderRight: `1px solid ${T.border}`,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    zIndex: 10,
  },
  sidebarTop: {
    padding: '22px 20px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    borderBottom: `1px solid ${T.border}`,
  },
  logoBox: {
    width: 28,
    height: 28,
    background: T.accent,
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 800,
    color: '#000',
    fontFamily: 'DM Mono, monospace',
  },
  logoName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '-0.02em' },
  navSection: { flex: 1, padding: '20px 12px 12px' },
  navLabel: {
    fontFamily: 'DM Mono, monospace',
    fontSize: 9,
    color: T.textMuted,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 10,
    paddingLeft: 8,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '9px 8px',
    background: 'transparent',
    border: 'none',
    color: T.textSec,
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: 3,
    marginBottom: 1,
    textAlign: 'left',
    transition: 'all 0.12s',
  },
  navItemActive: { background: T.accentSoft, color: T.accent },
  navMono: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, minWidth: 18 },
  navText: { flex: 1 },
  navArrow: { fontFamily: 'DM Mono, monospace', fontSize: 12, color: T.accent },
  sidebarFooter: {
    padding: '14px 12px',
    borderTop: `1px solid ${T.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  userRow: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' },
  userAva: {
    width: 30,
    height: 30,
    background: T.accentSoft,
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    color: T.accent,
    flexShrink: 0,
    fontFamily: 'DM Mono, monospace',
  },
  userInfo: { overflow: 'hidden' },
  userName: {
    fontSize: 12,
    fontWeight: 600,
    color: T.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userEmail: {
    fontSize: 10,
    color: T.textMuted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: 'DM Mono, monospace',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    color: T.textMuted,
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    flexShrink: 0,
  },
};
